<template>
    <HeaderComponent />
    <div class="container mx-auto p-4 h-full">
      <div>
        <h1 class="text-3xl font-bold text-center mb-8">Salesforce PDF Generator</h1>
  
        <div class="flex flex-col lg:flex-row gap-8">
          <div class="w-full lg:w-1/2">
            <h2 class="text-2xl font-semibold mb-4">PDF Settings</h2>
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="pdfType">
                Select PDF Type
              </label>
              <select
                id="pdfType"
                v-model="pdfType"
                @change="handleChange"
                class="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option v-for="type in Object.keys(fieldsByType)" :key="type" :value="type">
                  {{ type.charAt(0).toUpperCase() + type.slice(1) }}
                </option>
              </select>
            </div>
  
            <div v-if="pdfType">
              <label class="block text-gray-700 text-sm font-bold mb-2" for="fields">
                Select Fields
              </label>
              <select
                id="fields"
                v-model="selectedFields"
                multiple
                class="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                size="5"
                @change="handleChange"
              >
                <option v-for="field in fieldsByType[pdfType]" :key="field" :value="field">
                  {{ field }}
                </option>
              </select>
            </div>
  
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="style">
                Select Style
              </label>
              <select
                id="style"
                v-model="selectedStyle"
                @change="handleChange"
                class="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option v-for="style in Object.keys(styles)" :key="style" :value="style">
                  {{ style.charAt(0).toUpperCase() + style.slice(1) }}
                </option>
              </select>
            </div>
  
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="companyName">
                Company Name
              </label>
              <input
                type="text"
                id="companyName"
                v-model="companyName"
                @input="handleChange"
                class="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter company name"
              >
            </div>
  
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2" for="logoUrl">
                Logo URL
              </label>
              <input
                type="text"
                id="logoUrl"
                v-model="logoUrl"
                @input="handleChange"
                class="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter logo URL"
              >
            </div>
            <div class="mt-6">
              <button @click="generateCode" class="w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition duration-300">
                Generate Code
              </button>
            </div>
          </div>
  
          <div class="w-full lg:w-1/2">
            <div v-if="showPreview" >
              <h2 class="text-2xl font-semibold mb-4">Preview</h2>
              <div id="previewArea" :class="['preview-' + selectedStyle, 'border', 'rounded', 'p-4', 'overflow-x-auto']" class="mt-12">
                <div v-html="companyInfo" class="text-center mb-2"></div>
                <h3 class="text-xl font-bold mb-2">{{ objectName }} Information</h3>
                <table class="w-full border-collapse">
                  <tbody>
                    <tr v-for="field in selectedFields" :key="field" class="border-b">
                      <th class="py-2 px-4 text-left">{{ getFieldLabel(field) }}</th>
                      <td class="py-2 px-4">[{{ field }} value]</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

        </div>
  
        <transition name="fade">
          <div v-if="showCodePreview" class="mt-6">
            <div class="bg-gray-100 rounded p-4">
              <div class="flex justify-between items-center mb-2">
                <h2 class="text-xl font-bold">Visualforce Template</h2>
                <button @click="copyCode" class="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 transition duration-300">
                  Copy Code
                </button>
              </div>
              <div class="relative">
                <pre class="overflow-x-auto"><code class="language-markup" v-html="highlightedTemplate"></code></pre>
              </div>
            </div>
          </div>
        </transition>
      </div>

      <div class="max-w-4xl mx-auto">
        <!-- Header Section -->
        <div class="text-center mb-12 pt-16">
          <h1 class="text-3xl md:text-3xl font-bold mb-4 text-gray-900">
            PDF Generator  Guide
          </h1>
          <p class="text-lg text-gray-600 max-w-2xl mx-auto">
            Follow below steps to generate PDF using Salesforce PDF Generator Tool
          </p>
        </div>
        
        <!-- PDF Generator  Guide-->
        <div class="space-y-6 md:space-y-8">
            <ol>
                <li>
                    <span class="step-title">Step 1:</span> After selecting <strong>"PDF Builder"</strong> under Salesforce Tools, you'll be taken to the <strong>"Salesforce PDF Generator"</strong> page.
                    <img src="@/assets/redirect-2-pdf-generation.jpeg"  alt="select web-to-lead form builder">
                  </li>
                <li>
                    <span class="step-title">Step 4:</span> Here, you can customize the options to generate the pdf by:
                    <ul>
                        <li>&#x2022; Choosing the object for PDF.</li>
                        <li>&#x2022; Choosing the fields to include.</li>
                        <li>&#x2022; Choosing a custom style that determines the appearance of the PDF user interface.</li>
                    </ul>
                    <p>Additionally, you will need to provide your "Company Name" and "Logo URL" to include them in the PDF.</p>
                    <img src="@/assets/configured-pdf-generator.jpeg"  alt="select web-to-lead form builder">
                  </li>
                <li>
                    <span class="step-title">Step 5:</span> Click on <strong>"Generate Code"</strong> to create the Visualforce code for rendering "Visualforce Page" as a PDF. After the code is generated, you can copy it by clicking the <strong>"Copy Code"</strong> button below  and then embed it wherever you need.
                    <img src="@/assets/generated-pdf-code.jpeg"  alt="select web-to-lead form builder">
                </li>
                <li>
                    <span class="step-title">Step 6:</span> Once you've copied the code, simply paste it into your Visualforce page. 
                    For reference,
                    <a href="https://arrify.com/generate-pdf-in-salesforce" 
                      target="_blank" 
                      class="text-blue-500 hover:underline">
                      click here
                    </a>
                    to learn how to generate a PDF on button click through a Visualforce page.
                </li>
            </ol>
        </div>
      </div>

    </div>
    <FooterComponent />
  </template>
  
  <script>
  import { ref, computed, watch, onMounted } from 'vue'
  import Prism from 'prismjs'
  import 'prismjs/themes/prism.css'
  import 'prismjs/plugins/line-numbers/prism-line-numbers.js'
  import 'prismjs/plugins/line-numbers/prism-line-numbers.css'
  import {useCommonHead} from '../composables/useCommonHead';
  import {useCurrentUrl} from '../composables/useCurrentUrl';
  import { useWordPressMeta } from '../composables/useWordPressMeta'; // ADD THIS
  import HeaderComponent from "../components/Header.vue";
  import FooterComponent from "../components/Footer.vue";
  
  export default {
    name: 'SalesforcePDFGenerator',
    components: {
        HeaderComponent,
        FooterComponent,
    },
    setup() {
    const pageTitle = 'Salesforce PDF Builder'
    const pageDescription = 'Salesforce PDF Generator Tool'

      const currentUrl = useCurrentUrl();


      const { metaData, error, isLoading } = useWordPressMeta(
          currentUrl.value
      );

      const fieldsByType = {
        contact: ['FirstName', 'LastName', 'Email', 'Phone', 'Title', 'Department', 'MailingStreet', 'MailingCity', 'MailingState', 'MailingPostalCode', 'MailingCountry', 'AccountId', 'OwnerId', 'CreatedDate', 'LastModifiedDate'],
        account: ['Name', 'Industry', 'Type', 'BillingStreet', 'BillingCity', 'BillingState', 'BillingPostalCode', 'BillingCountry', 'Phone', 'Website', 'AnnualRevenue', 'NumberOfEmployees', 'OwnerId', 'CreatedDate', 'LastModifiedDate'],
        opportunity: ['Name', 'StageName', 'Amount', 'CloseDate', 'Probability', 'AccountId', 'OwnerId', 'Type', 'LeadSource', 'NextStep', 'Description'],
        lead: ['FirstName', 'LastName', 'Company', 'Email', 'Phone', 'Status', 'Rating', 'Industry', 'AnnualRevenue', 'NumberOfEmployees', 'LeadSource'],
        case: ['CaseNumber', 'Subject', 'Status', 'Priority', 'AccountId', 'ContactId', 'OwnerId', 'Type', 'Origin', 'Description', 'ClosedDate']
      }
  
      const styles = {
        classic: `
          body { font-family: Arial, sans-serif; color: #333; }
          .header { background-color: #f0f0f0; padding: 20px; border-bottom: 2px solid #999; }
          .content { padding: 20px; }
          h1 { color: #444; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #999; padding: 8px; text-align: left; }
          th { background-color: #f0f0f0; width: 30%; }
        `,
        modern: `
          body { font-family: 'Helvetica Neue', sans-serif; color: #2c3e50; }
          .header { background-color: #3498db; color: white; padding: 30px; }
          .content { padding: 30px; line-height: 1.6; }
          h1 { font-weight: 300; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #3498db; padding: 12px; text-align: left; }
          th { background-color: #3498db; color: white; width: 30%; }
        `,
        minimalist: `
          body { font-family: 'Roboto', sans-serif; color: #333; line-height: 1.6; }
          .header { border-bottom: 1px solid #eee; padding: 20px 0; }
          .content { padding: 30px 0; }
          h1 { font-weight: 100; color: #666; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: none; border-bottom: 1px solid #eee; padding: 10px; text-align: left; }
          th { font-weight: normal; color: #999; width: 30%; }
        `,
        colorful: `
          body { font-family: 'Open Sans', sans-serif; color: #333; background-color: #f9f9f9; }
          .header { background-color: #e74c3c; color: white; padding: 30px; }
          .content { padding: 30px; background-color: white; box-shadow: 0 0 10px rgba(0,0,0,0.1); }
          h1 { color: #2ecc71; }
          table { width: 100%; border-collapse: separate; border-spacing: 0 10px; }
          th, td { border: none; padding: 15px; text-align: left; }
          th { background-color: #3498db; color: white; width: 30%; }
          tr { background-color: #ecf0f1; }
        `,
        salesforce: `
        body { font-family: 'Salesforce Sans', Arial, sans-serif; color: #16325c; }
        .header { background-color: #0070d2; color: white; padding: 1rem; }
        .content { padding: 1rem; }
        h1 { font-weight: 300; }
        table { width: 100%; border-collapse: separate; border-spacing: 0; }
        th, td { border: 1px solid #d8dde6; padding: 0.5rem; }
        th { background-color: #f3f2f2; font-weight: 700; }
      `
      }
  
      const pdfType = ref('contact')
      const selectedFields = ref(['FirstName', 'LastName', 'Email', 'Phone'])
      const selectedStyle = ref('salesforce')
      const companyName = ref('')
      const logoUrl = ref('')
  
      const showPreview = computed(() => pdfType.value && selectedFields.value.length > 0)
  
      const objectName = computed(() => pdfType.value.charAt(0).toUpperCase() + pdfType.value.slice(1))
  
      const companyInfo = computed(() => {
        let html = ''
        if (companyName.value || logoUrl.value) {
          html += '<div class="flex flex-col items-center">'
          if (logoUrl.value) {
            html += `<img src="${logoUrl.value}" alt="Company Logo" class="max-w-xs max-h-24 mb-2"><br>`
          }
          if (companyName.value) {
            html += `<h2 class="text-xl font-bold">${companyName.value}</h2>`
          }
          html += '</div>'
        }
        return html
      })
  
      const visualforceTemplate = computed(() => {
        if (!showPreview.value) return ''
  
        return `
  <apex:page standardController="${pdfType.value}" renderAs="pdf" showHeader="false"
             sidebar="false" standardStylesheets="false" applyHtmlTag="false">
    <head>
      <style>
        ${styles[selectedStyle.value]}
      </style>
    </head>
   
    <div class="header">
      ${companyName.value ? `<h2 style="text-align: center;">${companyName.value}</h2>` : ''}
      ${logoUrl.value ? `<div style="text-align: center;"><img src="${logoUrl.value}" alt="Company Logo" style="max-width: 200px; max-height: 100px;"/></div>` : ''}
      <h1>${pdfType.value.charAt(0).toUpperCase() + pdfType.value.slice(1)} Information</h1>
    </div>
    <div class="content">
      <table>
        ${selectedFields.value.map(field => `
          <tr>
            <th>{!$ObjectType.${pdfType.value}.Fields.${field}.Label}</th>
            <td><apex:outputField value="{!${pdfType.value}.${field}}"/></td>
          </tr>
        `).join('')}
      </table>
    </div>
  </apex:page>`
      })
  
      const updateFieldOptions = () => {
        selectedFields.value = []
      }
  
      const getFieldLabel = (field) => {
        return `{!$ObjectType.${objectName.value}.Fields.${field}.Label}`
      }
  
      const highlightedTemplate = computed(() => {
        if (!showPreview.value) return ''
        return Prism.highlight(visualforceTemplate.value, Prism.languages.html, 'html')
      })
  
      const copyCode = () => {
        navigator.clipboard.writeText(visualforceTemplate.value)
          .then(() => alert('Code copied to clipboard!'))
          .catch(err => console.error('Failed to copy code: ', err))
      }
  
      const showCodePreview = ref(false)
  
      const generateCode = () => {
        showCodePreview.value = true
      }
  
      const handleChange = () => {
        if (showCodePreview.value) {
          showCodePreview.value = false
        }
      }
  
      onMounted(() => {
        Prism.highlightAll()
      })
  
      watch(pdfType, updateFieldOptions)
  
      return {
        fieldsByType,
        styles,
        pdfType,
        selectedFields,
        selectedStyle,
        companyName,
        logoUrl,
        showPreview,
        objectName,
        companyInfo,
        visualforceTemplate,
        updateFieldOptions,
        getFieldLabel,
        highlightedTemplate,
        copyCode,
        showCodePreview,
        generateCode,
        handleChange
      }
    }
  }
  </script>
  
  <style>
  @import 'tailwindcss/base';
  @import 'tailwindcss/components';
  @import 'tailwindcss/utilities';

  /* Classic theme */
#previewArea.preview-classic {
  background-color: #f8f8f8;
  font-family: Arial, sans-serif;
  color: #333;
}

#previewArea.preview-classic h3 {
  color: #444;
  font-size: 1.25rem;
  border-bottom: 2px solid #999;
  padding-bottom: 0.5rem;
}

#previewArea.preview-classic th,
#previewArea.preview-classic td {
  border: 1px solid #999;
}

#previewArea.preview-classic th {
  background-color: #f0f0f0;
  width: 30%;
}

/* Modern theme */
#previewArea.preview-modern {
  background-color: white;
  font-family: 'Helvetica Neue', sans-serif;
  color: #2c3e50;
}

#previewArea.preview-modern h3 {
  color: #3498db;
  font-size: 1.5rem;
  font-weight: 300;
}

#previewArea.preview-modern th,
#previewArea.preview-modern td {
  border: 1px solid #3498db;
  padding: 12px;
}

#previewArea.preview-modern th {
  background-color: #3498db;
  color: white;
  width: 30%;
}

/* Minimalist theme */
#previewArea.preview-minimalist {
  background-color: white;
  font-family: 'Roboto', sans-serif;
  color: #333;
  border: none;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24);
}

#previewArea.preview-minimalist h3 {
  font-weight: 100;
  color: #666;
  font-size: 1.5rem;
  margin-bottom: 1rem;
}

#previewArea.preview-minimalist th,
#previewArea.preview-minimalist td {
  border: none;
  border-bottom: 1px solid #eee;
  padding: 10px;
}

#previewArea.preview-minimalist th {
  font-weight: normal;
  color: #999;
  width: 30%;
}

/* Colorful theme */
#previewArea.preview-colorful {
  background-color: #f9f9f9;
  font-family: 'Open Sans', sans-serif;
  color: #333;
  border: none;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
}

#previewArea.preview-colorful h3 {
  color: #2ecc71;
  font-size: 1.5rem;
  margin-bottom: 1rem;
  text-align: center;
  background-color: #e74c3c;
  color: white;
  padding: 10px;
  margin: -1rem -1rem 1rem -1rem;
  border-radius: 4px 4px 0 0;
}

#previewArea.preview-colorful table {
  border-collapse: separate;
  border-spacing: 0 10px;
}

#previewArea.preview-colorful th,
#previewArea.preview-colorful td {
  border: none;
  padding: 15px;
}

#previewArea.preview-colorful th {
  background-color: #3498db;
  color: white;
  width: 30%;
}

#previewArea.preview-colorful tr {
  background-color: #ecf0f1;
}

/* Salesforce theme */
#previewArea.preview-salesforce {
  background-color: white;
  font-family: 'Salesforce Sans', Arial, sans-serif;
  color: #16325c;
}

#previewArea.preview-salesforce h3 {
  color: #16325c;
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #d8dde6;
}

#previewArea.preview-salesforce table {
  border-collapse: separate;
  border-spacing: 0;
}

#previewArea.preview-salesforce th,
#previewArea.preview-salesforce td {
  border: 1px solid #d8dde6;
  padding: 0.5rem;
}

#previewArea.preview-salesforce th {
  background-color: #f3f2f2;
  font-weight: 700;
  color: #16325c;
}

/* Company info styles */
#previewArea .company-info {
  text-align: center;
  margin-bottom: 20px;
}

#previewArea .company-info img {
  max-width: 200px;
  max-height: 100px;
  margin-bottom: 10px;
}

#previewArea .company-info h2 {
  font-size: 1.5rem;
  margin-bottom: 0;
}
  
  /* Transitions for smooth show/hide of code preview */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }
  
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  li {
      margin-bottom: 5px;
  }
  ul {
      margin-left: 20px;
  }
  .step-title {
      font-weight: bold;
      color: #0073e6;
  }
  .note {
      font-style: italic;
      color: #555;
  }
  </style>
